import { ArrowUpRight, ImageOff } from 'lucide-react'
import Link from 'next/link'
import { renderRichText } from './richText'
import type {
  HubGuidePathwayItem,
  HubGuidePathwaysSection,
  HubReportRef,
  HubSectionComponentProps,
} from './types'

interface ResolvedPathway {
  item: HubGuidePathwayItem
  report: HubReportRef
}

/**
 * Report-backed pathways laid out editorially: the first pathway is a wide
 * featured unit (copy beside a large hero image) and the rest fall into a
 * responsive grid beneath it. The grid wraps, so any pathway count past the
 * first renders cleanly (4-up on large screens) without a hard cap.
 *
 * The whole card is clickable via an overlay link (last child, `absolute
 * inset-0`) that covers the card and carries the report href. Inline report
 * anchors inside the body are raised above the overlay (`relative z-10`) so they
 * stay independently clickable — no nested anchors.
 */

function PathwayImage({ report }: { report: HubReportRef }) {
  if (!report.imageUrl) {
    return (
      <div className='absolute inset-0 flex items-center justify-center bg-ds-bg-secondary'>
        <ImageOff className='size-8 text-ds-fg-quaternary' strokeWidth={1.5} />
      </div>
    )
  }

  return (
    <img
      src={report.imageUrl}
      alt={report.title}
      loading='lazy'
      className='absolute inset-0 h-full w-full object-cover mix-blend-darken transition-transform duration-300 group-hover:scale-[1.03] dark:mix-blend-normal'
    />
  )
}

function PathwayCta({ item }: { item: HubGuidePathwayItem }) {
  return (
    <span className='mt-auto inline-flex items-center gap-1 pt-1 text-sm font-semibold text-info-700 group-hover:underline'>
      {item.linkLabel ?? 'Read report'}
      <ArrowUpRight
        className='size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5'
        aria-hidden
      />
    </span>
  )
}

function PathwayOverlayLink({ item, report }: ResolvedPathway) {
  return (
    <Link
      href={report.href}
      aria-label={
        item.linkLabel ? `${item.heading}: ${item.linkLabel}` : item.heading
      }
      className='absolute inset-0 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info-500'
    />
  )
}

function FeaturedPathway({ item, report }: ResolvedPathway) {
  return (
    <article className='group relative grid overflow-hidden rounded-2xl border border-ds-border-primary bg-ds-bg-primary transition-colors hover:border-ds-text-primary md:grid-cols-2'>
      <div className='order-2 flex flex-col gap-3 p-6 md:order-1 md:justify-center md:p-8'>
        <h3 className='font-display text-2xl font-bold leading-8 text-ds-text-primary md:text-3xl md:leading-9'>
          {item.heading}
        </h3>
        {item.subheading && (
          <p className='text-base font-medium leading-6 text-ds-text-secondary'>
            {item.subheading}
          </p>
        )}
        <p className='text-base leading-7 text-ds-text-secondary [&_a]:relative [&_a]:z-10'>
          {renderRichText(item.body)}
        </p>
        <PathwayCta item={item} />
      </div>

      <div className='relative order-1 min-h-[220px] overflow-hidden bg-maxx-warm md:order-2'>
        <PathwayImage report={report} />
      </div>

      <PathwayOverlayLink item={item} report={report} />
    </article>
  )
}

function GridPathway({ item, report }: ResolvedPathway) {
  return (
    <article className='group relative flex flex-col overflow-hidden rounded-2xl border border-ds-border-primary bg-ds-bg-primary transition-colors hover:border-ds-text-primary'>
      <div className='relative aspect-[16/10] overflow-hidden bg-maxx-warm'>
        <PathwayImage report={report} />
      </div>
      <div className='flex flex-1 flex-col gap-2 p-5'>
        <h4 className='line-clamp-2 font-display text-lg font-semibold leading-6 text-ds-text-primary'>
          {item.heading}
        </h4>
        <p className='line-clamp-3 text-sm leading-6 text-ds-text-secondary [&_a]:relative [&_a]:z-10'>
          {renderRichText(item.body)}
        </p>
        <PathwayCta item={item} />
      </div>

      <PathwayOverlayLink item={item} report={report} />
    </article>
  )
}

export function HubGuidePathways({
  section,
  context,
}: HubSectionComponentProps<HubGuidePathwaysSection>) {
  const pathways = section.pathways
    .map((item) => ({
      item,
      report: context.reportsById[item.reportId],
    }))
    .filter((entry): entry is ResolvedPathway => Boolean(entry.report))

  if (pathways.length === 0) return null

  const [featured, ...rest] = pathways

  return (
    <section id={section.id} className='flex flex-col gap-6'>
      <div className='max-w-3xl'>
        {section.eyebrow && (
          <p className='text-xs font-semibold uppercase tracking-wide text-ds-text-quaternary'>
            {section.eyebrow}
          </p>
        )}
        {section.title && (
          <h2 className='mt-1 font-display text-2xl font-bold leading-8 text-foreground'>
            {section.title}
          </h2>
        )}
        {section.dek && (
          <p className='mt-2 text-base leading-6 text-foreground-secondary'>
            {section.dek}
          </p>
        )}
      </div>

      <div className='flex flex-col gap-6'>
        {featured && (
          <FeaturedPathway item={featured.item} report={featured.report} />
        )}
        {rest.length > 0 && (
          <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
            {rest.map(({ item, report }) => (
              <GridPathway
                key={`${item.reportId}-${item.heading}`}
                item={item}
                report={report}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
